import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, Icon } from "@/ds";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  anonymousKeyFor, executeAction, forgetAnonymousKey, rejectAction,
} from "@/lib/dany";
import { budgetText } from "@/lib/jobDisplay";
import { jobDetailPath } from "@/lib/ownerRoutes";
import { INVITE_CLIENT_FIRST_JOB, armPushInvite } from "@/lib/push";
import DanySignupCard from "@/components/dany/DanySignupCard";
import DanyPhotoStrip from "@/components/dany/DanyPhotoStrip";
import { formatDate } from "@/lib/dates";

/**
 * The commit point of DANY.md §4, as the client sees it (DY2.1, staged in DY2.3).
 *
 * Dany proposes; this widget is where a person decides. Everything it renders
 * comes from `tool_result.data` — the draft, the `action_id`, and which fields
 * are still missing — so a reloaded transcript shows the same card the live
 * stream did, which is the rule every Dany widget follows.
 *
 * **The button is not the security boundary and does not pretend to be.** The
 * backend refuses to execute without the `action_id` this card was given, checks
 * that the caller owns the conversation it came from, and expires it. What the
 * button does is make the decision *legible*: nothing here is committed until a
 * person presses it, and a person who presses it has read what they are
 * committing.
 *
 * **It used to collect, and now it only shows** (N9, Jorge 2026-09-19).
 *
 * The history is worth keeping because the reasons still hold. It began as one
 * card with nine inputs and a Publish button, with the model gathering values
 * in the chat before the card existed at all — a visitor on a phone answered
 * three questions to see anything, then met a form whose lower half was under
 * the keyboard. DY2.3 broke that into stages, one decision per screen, each
 * pre-filled from the conversation:
 *
 *     describe -> location -> review -> [signup] -> published
 *
 * What N9 changes is not the funnel but who writes. Two things could put a
 * value on this draft — the person typing into a stage, and Dany writing what
 * they dictated — and the two raced: N33 measured a dictated description never
 * reaching the card at all, so what would have been published was an inference
 * from two turns earlier. Two writers, one row, and the person cannot see
 * which one won.
 *
 * So the capture stages are gone and `review` is the whole card: it shows the
 * draft, says what is still missing, and offers three things — Publish,
 * Discard, and **Ask Dany to change it**, which puts the cursor in the
 * composer. Saying it is the only way to change it, which means there is
 * exactly one writer and the card always shows what would be published.
 *
 * Two fields stay editable and neither is a field of the draft: registration
 * (an account is not a job) and the photo control, which the spec names as its
 * one exception because uploading is not something you can dictate.
 *
 * `propose→confirm→execute` is untouched: one `action_id`, minted by the tool
 * or by `_live_draft` when a conversation is reopened, spent by the button —
 * and `execute_action` keeps only the keys the tool proposed.
 */

// How long the receipt stays on screen before the app takes them to the job.
// Four seconds, Jorge, 2026-08-29: long enough to read the sentence that sets
// the expectation ("the FixyBees near you have been notified"), short enough
// that nobody wonders whether anything happened. The manual button is present
// from the first frame, so this is a convenience and never the only way out.
const NAVIGATE_AFTER_MS = 4000;

function Row({ icon, children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <Icon name={icon} size={11} />{children}
    </span>
  );
}

export default function DanyJobDraftWidget({ data, threadId, onCommitted }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  // N9. Read-only now: nothing on this card writes to it. Kept as state
  // rather than read straight from `data` because publishing rewrites the
  // card into a receipt, and the stages below still read the draft after the
  // row behind it has moved on.
  const [draft] = React.useState(() => ({ ...(data?.draft || {}) }));
  const [busy, setBusy] = React.useState(false);
  // "review" is the only capture stage left. `describe` and `location` were
  // the two that collected fields, and a card that does not collect has no
  // use for either — see the note above the budget block.
  const [stage, setStage] = React.useState("review");
  const [result, setResult] = React.useState(null);
  // N9 (e). Discarding is destructive and nothing brings the draft back, so
  // the button asks once before it does anything. Two taps rather than an
  // `undo`, because an undo would need the row to survive the act it undoes —
  // and `discarded` is terminal in `services/dany/drafts.py` on purpose.
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [verifyEmail, setVerifyEmail] = React.useState(null);
  // null = we never registered here and cannot know; false = Resend refused the
  // message and telling this person to check their inbox would be a lie.
  const [emailSent, setEmailSent] = React.useState(null);
  const [resent, setResent] = React.useState(null);

  const actionId = data?.action_id;
  // Served by the tool and by `_live_draft`, recomputed on the server on every
  // render of either, never stored (N27). The card shows it and the Publish
  // button obeys it, so the legend and the button cannot disagree.
  const missing = React.useMemo(() => data?.missing || [], [data]);

  /**
   * The one way to change something on this card: say it.
   *
   * Puts the cursor in the composer instead of opening a field, because a
   * field here would be the second source of truth the whole of N9 exists to
   * remove — and while Dany is the only writer, what she wrote is what gets
   * published. On a call there is nothing to focus and nothing to do: saying
   * it already works, and this button is only the reminder that it does.
   */
  const askDany = React.useCallback(() => {
    const box = document.querySelector("[data-dany-composer]");
    if (box) {
      box.focus();
      box.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, []);

  // A transcript reloaded after the job went live already carries the published
  // card as its own tool row, so this one stays a draft. What must not happen is
  // this card offering Publish again for a proposal that is already spent — the
  // server refuses it, and the refusal is a worse experience than not offering.
  const settled = stage === "published" || stage === "verify";

  const hasPlace = !!(draft.zip_code || draft.city || draft.state);

  const localProblem = React.useMemo(() => {
    const title = (draft.title || "").trim();
    const description = (draft.description || "").trim();
    if (title.length < 3) return t("dany.job.errorTitleShort");
    if (description.length < 10) return t("dany.job.errorDescriptionShort");
    // No trade on the draft. The card used to say "Pick a category so the right
    // FixyBees see it" — an instruction with nothing on this card to carry it
    // out with, and one the client should never receive anyway: they describe,
    // FixyDany classifies (`_infer_draft_category` in services/dany/tools.py
    // has already tried, on the same classifier the job form runs). What is
    // actually missing here is problem, not taxonomy, so that is what it asks
    // for — the same thing Dany asks for out loud in the same situation.
    if (!draft.category_id) return t("dany.job.needMoreDetail");
    // The server refuses this one outright (`draft_needs_location`), so the
    // button is disabled rather than letting someone press it and be told no.
    // A job with no place cannot be geocoded or matched — it is not a job.
    if (!hasPlace) return t("dany.job.draft_needs_location");
    const min = Number(draft.budget_min);
    const max = Number(draft.budget_max);
    if (Number.isFinite(min) && Number.isFinite(max) && max < min) {
      return t("dany.job.errorBudgetRange");
    }
    if (draft.preferred_start_date && draft.preferred_end_date
        && draft.preferred_end_date < draft.preferred_start_date) {
      return t("dany.job.errorDateOrder");
    }
    // Compared as ISO strings against the browser's local day, which is the
    // right clock HERE: this is a hint for the person editing the card, and the
    // server's own clock is what actually decides.
    const localToday = new Date();
    const today = [
      localToday.getFullYear(),
      String(localToday.getMonth() + 1).padStart(2, "0"),
      String(localToday.getDate()).padStart(2, "0"),
    ].join("-");
    if ((draft.preferred_start_date && draft.preferred_start_date < today)
        || (draft.preferred_end_date && draft.preferred_end_date < today)) {
      return t("dany.job.draft_dates_in_the_past");
    }
    return null;
  }, [draft, hasPlace, t]);

  /**
   * Commit the draft. Called twice in the funnel: once by the Publish button,
   * and once again by `DanySignupCard` with the account it just created.
   *
   * **The anonymous key has to survive registration by exactly one request.**
   * Claiming happens inside this call — `execute` sets the thread's `user_id`
   * and clears the key server-side — so until it returns, the key in the
   * browser is the only thing tying the brand-new account to the conversation
   * the proposal came from. `62a9238` deleted it in the signup component, one
   * line before this ran, and production answered the retry with:
   *
   *     POST /dany/actions/1f9b1bf5-…/execute   (session cookie, no key)
   *     404 {"code":"action_not_found"}
   *
   * for every inline registration, Google and email alike. So the key is
   * passed in by the caller that still has it, and forgotten *after* the
   * server has told us the thread now belongs to the account.
   */
  const publish = React.useCallback(async (options) => {
    if (!actionId) return;
    const { anonymousKey, verificationSent } = options || {};
    if (typeof verificationSent === "boolean") setEmailSent(verificationSent);
    setBusy(true);
    setError(null);
    try {
      const payload = await executeAction(actionId, draft, {
        anonymousKey: anonymousKey || anonymousKeyFor(threadId),
      });
      forgetAnonymousKey(threadId);
      setResult(payload.job || null);
      setStage("published");
      // The same moment the job form arms (PostJob.jsx): their job is live
      // and quotes are what happens next. Before T20-M2 publishing from the
      // thread armed nothing, so a person who never used the form never heard
      // the sentence that is about them. Collected right away by PushOptIn,
      // which waits out the thread's shell and speaks on the first screen
      // that is not one — the job page this card navigates to.
      armPushInvite(INVITE_CLIENT_FIRST_JOB);
      // The thread re-reads itself: this card is not the only thing on
      // screen that this press changed (T11 N38).
      onCommitted?.();
      // Publishing is also what gives a brand-new account its role, server-side
      // (`routes.dany._complete_client_profile`), and the profile this app is
      // holding was captured a moment earlier by `POST /auth/google`, when the
      // role was still null. `ProtectedRoute` reads that cached profile and
      // sends anyone without a role to a sign-up page — which is where Jorge and
      // Daniel's Google walkthrough landed, on "client or FixyBee?", a question
      // they had just spent five minutes answering. One GET fixes it, and it
      // has to finish before the navigation below.
      try { await refresh(); } catch { /* the job is posted either way */ }
    } catch (err) {
      if (err.code === "registration_required") {
        // No account yet, so nothing was claimed and the key is still the only
        // way back to this proposal. Keeping it is the whole point.
        setStage("signup");
      } else if (err.code === "email_verification_required") {
        forgetAnonymousKey(threadId);
        setVerifyEmail(err.email || null);
        setStage("verify");
      } else {
        setError(
          t([`dany.job.${err.code}`, `dany.errors.${err.code}`,
             "dany.job.publishFailed"], {
            defaultValue: t("dany.job.publishFailed"),
          })
        );
      }
    } finally {
      setBusy(false);
    }
  }, [actionId, draft, threadId, t, refresh, onCommitted]);

  // The last step of the flow, and the only automatic navigation in Dany.
  // Cleared on unmount so a person who taps the button first is not navigated
  // twice, and so leaving the thread cancels it.
  const jobId = result?.job_id;
  React.useEffect(() => {
    if (stage !== "published" || !jobId) return;
    const id = setTimeout(
      () => navigate(jobDetailPath(user?.profile?.role, jobId)), NAVIGATE_AFTER_MS);
    return () => clearTimeout(id);
  }, [stage, jobId, navigate]);

  /**
   * Throw the draft away, on the second tap.
   *
   * `rejectAction` now closes the `anonymous_drafts` row as well as the
   * proposal (N9 e), which is what makes one gesture clear both projections:
   * the card here and the "Borrador — sin publicar" entry in the dashboard are
   * two readings of that row, so neither can survive the other.
   */
  const discard = async () => {
    if (!actionId) return;
    if (!confirmDiscard) { setConfirmDiscard(true); return; }
    setBusy(true);
    try {
      await rejectAction(actionId, { anonymousKey: anonymousKeyFor(threadId) });
      setStage("discarded");
      // The thread re-reads itself: this card is not the only thing on
      // screen that this press changed (T11 N38).
      onCommitted?.();
    } catch {
      setStage("discarded");
      // The thread re-reads itself: this card is not the only thing on
      // screen that this press changed (T11 N38).
      onCommitted?.();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    // The endpoint reports what Resend actually did now, so this button can stop
    // claiming success on a message that was refused.
    try {
      const { data: sent } = await api.post("/auth/resend-verification", {});
      setResent(!!sent.sent);
      setEmailSent(!!sent.sent);
    } catch {
      setResent(false);
      setEmailSent(false);
    }
  };

  // Discarded here, by this card's own button: it simply goes.
  if (stage === "discarded") return null;

  // Discarded by Dany, because they said so out loud (N9 e). The tool's result
  // arrives as one more card, which `dropSuperseded` uses to replace the draft
  // — the same mechanism a publish uses for its receipt. Said rather than
  // silent, because a card that vanished with no explanation reads as a bug.
  if (data?.discarded) {
    return (
      <Card variant="raised" padding="md" data-testid="dany-job-discarded">
        <p style={heading}>{t("dany.job.discardedTitle")}</p>
        <p style={body}>{t("dany.job.discardedBody")}</p>
      </Card>
    );
  }

  if (stage === "verify") {
    return (
      <Card variant="raised" padding="md" data-testid="dany-job-verify">
        <p style={heading}>
          {emailSent === false
            ? t("dany.signup.verifyNotSentHeader")
            : t("dany.signup.verifyHeader")}
        </p>
        <p style={body}>
          {emailSent === false
            ? t("dany.signup.verifyNotSentBody")
            : t("dany.signup.verifyBody", { email: verifyEmail || "" })}
        </p>
        <div style={{ marginTop: "var(--space-4)", display: "flex", gap: "var(--space-2)" }}>
          <Button variant="ghost" size="sm" icon="mail" onClick={resend}
                  data-testid="dany-job-verify-resend">
            {resent === true
              ? t("dany.signup.verifySent")
              : t("dany.signup.verifyResend")}
          </Button>
        </div>
        {resent === false && (
          <p style={{ ...body, color: "var(--danger)" }}
             data-testid="dany-job-verify-failed">
            {t("dany.signup.verifyNotSentBody")}
          </p>
        )}
      </Card>
    );
  }

  if (stage === "published") {
    const place = [draft.city, draft.state].filter(Boolean).join(", ");
    const category = draft.category_name || draft.category_id || "";
    const flagged = result?.moderation_status === "flagged";
    return (
      <Card variant="raised" padding="md" data-testid="dany-job-published">
        <p style={heading}>{t("dany.job.publishedTitle")}</p>
        <p style={body}>
          {place
            ? t("dany.job.publishedBody", { category, place })
            : t("dany.job.publishedBodyNoPlace", { category })}
        </p>
        {/* The expectation, in its own line and its own words: who was told,
            and what happens next. Without it the receipt says a job exists and
            leaves the person wondering whether to do something else. */}
        <p style={{ ...body, color: "var(--text-body)" }}
           data-testid="dany-job-expectation">
          {t("dany.job.publishedExpectation")}
        </p>
        {flagged && (
          <p style={{ ...body, color: "var(--text-muted)" }}
             data-testid="dany-job-flagged">
            {t("dany.job.moderationFlagged")}
          </p>
        )}
        {jobId && (
          <Button
            variant="secondary" size="md" iconRight="arrow-right"
            style={{ marginTop: "var(--space-4)" }}
            onClick={() => navigate(jobDetailPath(user?.profile?.role, jobId))}
            data-testid="dany-job-view"
          >{t("dany.job.viewJob")}</Button>
        )}
      </Card>
    );
  }

  const header = (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: "var(--space-2)",
    }}>
      <span style={eyebrow}>
        <Icon name="sparkles" size={12} style={{ color: "var(--hive-ink)" }} />
        {t("dany.job.draftHeader")}
      </span>
      {draft.category_name && (
        <Badge tone="neutral">
          {draft.subcategory_name
            ? `${draft.category_name} · ${draft.subcategory_name}`
            : draft.category_name}
        </Badge>
      )}
    </div>
  );

  // ---- Stage 3: the whole post, and the button ---------------------------
  return (
    <Card variant="raised" padding="md" data-testid="dany-job-draft">
      {header}
      <h3 style={{
        marginTop: "var(--space-3)", fontFamily: "var(--font-display)",
        fontWeight: "var(--weight-bold)", fontSize: "var(--text-xl)",
        color: "var(--text-body)", letterSpacing: "var(--tracking-tight)",
      }} data-testid="dany-job-review-title">{draft.title}</h3>
      {draft.description && (
        <p style={{
          marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
          color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
          maxWidth: "var(--measure-body)", whiteSpace: "pre-wrap",
        }}>{draft.description}</p>
      )}
      {/* T19 F4 (D5). The draft is born with the photos the conversation
          carries — attached in the thread, put here by the server, published
          with the job. Shown, not edited: like every other field of this card,
          the way to change them is to say so (or attach another). */}
      {(draft.photo_urls || []).length > 0 && (
        <div style={{ marginTop: "var(--space-3)" }}>
          <DanyPhotoStrip urls={draft.photo_urls} size={64} testid="dany-job-draft-photos" />
        </div>
      )}
      <div style={{
        marginTop: "var(--space-4)", display: "flex", gap: "var(--space-4)",
        flexWrap: "wrap", fontFamily: "var(--font-mono)",
        fontSize: "var(--text-xs)", color: "var(--text-muted)",
      }}>
        {(draft.city || draft.state || draft.zip_code) && (
          <Row icon="map-pin">
            {[[draft.city, draft.state].filter(Boolean).join(", "),
              draft.zip_code].filter(Boolean).join(" · ")}
          </Row>
        )}
        <Row icon="wallet">{budgetText(t, draft.budget_min, draft.budget_max)}</Row>
        <Row icon="calendar">
          {draft.preferred_start_date
            ? t("dany.job.datesRange", {
                start: formatDate(draft.preferred_start_date),
                end: formatDate(draft.preferred_end_date || draft.preferred_start_date),
              })
            : t("dany.job.datesFlexible")}
        </Row>
      </div>

      {/* N9. The budget is shown, never typed here. It was two number inputs
          on this card, which is the last place that was still true and is why
          the spec named this conversion explicitly: a card that collects is a
          second source of truth racing the one Dany writes, and the person
          ends up publishing whichever of the two happened to win. It is said
          out loud now, like every other field.

          Still optional, and the line below still says so: a budget was
          required until DY2.3 and it was the question that cost the most
          people, because a FixyBee quotes the job either way. */}
      <p style={{ ...body, color: "var(--text-subtle)", marginTop: 0 }}>
        {t("dany.job.budgetHint")}
      </p>

      {/* (c) The legend, computed from the live draft on every render and
          never stored, so it dies the moment the field arrives. The card and
          the dashboard entry read the same list and name a field with the same
          word. */}
      {missing.length > 0 && (
        <p style={{ ...body, color: "var(--text-subtle)" }}
           data-testid="dany-job-missing">
          {t("dany.job.missing", {
            fields: missing
              .map((f) => t(`dany.job.field${f.charAt(0).toUpperCase()}${f.slice(1)}`, f))
              .join(", "),
          })}
        </p>
      )}

      {(localProblem || error) && (
        <p style={{ ...body, color: "var(--danger)" }} data-testid="dany-job-problem">
          {error || localProblem}
        </p>
      )}

      {!settled && stage !== "signup" && (
        <div style={{
          marginTop: "var(--space-5)", display: "flex", gap: "var(--space-2)",
          flexWrap: "wrap",
        }}>
          <Button
            variant="primary" size="md" iconRight="arrow-right"
            disabled={busy || !!localProblem || !actionId || missing.length > 0}
            onClick={() => publish()} data-testid="dany-job-publish"
          >{busy ? t("dany.job.publishing") : t("dany.job.publish")}</Button>
          {/* Replaces "Back", which only existed to walk between the capture
              stages. Nothing here edits, so there is nowhere to go back TO —
              what there is instead is the one way to change something: say it.
              On a call, saying it is already enough and this button is just a
              reminder that it is. */}
          <Button variant="ghost" size="sm" icon="message-circle" disabled={busy}
                  onClick={askDany} data-testid="dany-job-ask-change">
            {t("dany.job.askChange")}
          </Button>
          <Button variant={confirmDiscard ? "danger" : "ghost"} size="sm" icon="x"
                  disabled={busy} onClick={discard}
                  data-testid="dany-job-discard">
            {confirmDiscard ? t("dany.job.discardConfirm") : t("dany.job.discard")}
          </Button>
          {confirmDiscard && (
            <Button variant="ghost" size="sm" disabled={busy}
                    onClick={() => setConfirmDiscard(false)}
                    data-testid="dany-job-discard-cancel">
              {t("dany.job.discardCancel")}
            </Button>
          )}
        </div>
      )}

      {stage === "signup" && !user && (
        <DanySignupCard
          threadId={threadId}
          busy={busy}
          setBusy={setBusy}
          // Registration done: the same action id, now with a session behind it.
          // The backend claims the thread on this call, so the conversation
          // follows the account without the user doing anything.
          onRegistered={publish}
        />
      )}
    </Card>
  );
}

const eyebrow = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};

const heading = {
  fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
  fontSize: "var(--text-lg)", color: "var(--text-body)",
};

const body = {
  marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
  color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
  maxWidth: "var(--measure-body)",
};
